import type {
    AdminContentItemRpcRow,
    CanonicalContentType
} from "../../services/backend/database.types.js";
import { useAdminText } from "./adminI18n.js";

import {
    Badge,
    Input,
    Select
} from "../../ui/components/Controls.js";

import {
    EmptyState,
    ErrorState,
    LoadingState
} from "../../ui/components/Feedback.js";

const contentTypeLabels: Record<CanonicalContentType, string> = {
    grammar_lesson: "Grammaire",
    news_article: "Actualité",
    home_feature: "Bloc d’accueil",
    travel_lesson: "Voyage",
    vocabulary_pack: "Vocabulaire"
};

const contentTypes = Object.keys(
    contentTypeLabels
) as CanonicalContentType[];

function ContentFilters({ query, typeFilter, levelFilter, onQuery, onTypeFilter, onLevelFilter }: {
    query: string;
    typeFilter: CanonicalContentType | "all";
    levelFilter: string;
    onQuery: (value: string) => void;
    onTypeFilter: (value: CanonicalContentType | "all") => void;
    onLevelFilter: (value: string) => void;
}) {
    const adminText = useAdminText();
    return (
        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <label>
                <span className="sr-only">{adminText("Rechercher un contenu")}</span>
                <Input
                    type="search"
                    value={query}
                    placeholder={adminText("Titre ou identifiant…")}
                    onChange={event => onQuery(event.target.value)}
                />
            </label>
            <label>
                <span className="sr-only">{adminText("Filtrer par type")}</span>
                <Select
                    value={typeFilter}
                    onChange={event => onTypeFilter(
                        event.target.value as CanonicalContentType | "all"
                    )}
                >
                    <option value="all">{adminText("Tous les types")}</option>
                    {contentTypes.map(type => (
                        <option key={type} value={type}>
                            {adminText(contentTypeLabels[type])}
                        </option>
                    ))}
                </Select>
            </label>
            {typeFilter === "grammar_lesson" ? (
                <label>
                    <span className="sr-only">{adminText("Filtrer la grammaire par niveau")}</span>
                    <Select
                        aria-label={adminText("Filtrer la grammaire par niveau")}
                        value={levelFilter}
                        onChange={event => onLevelFilter(event.target.value)}
                    >
                        <option value="all">{adminText("Tous les niveaux")}</option>
                        {["A1", "A2", "B1", "B2", "C1"].map(level => (
                            <option key={level} value={level}>{level}</option>
                        ))}
                    </Select>
                </label>
            ) : null}
        </div>
    );
}

function ContentInventory({ error, items, loading, onRetry, onSelect }: {
    error: boolean;
    items: AdminContentItemRpcRow[];
    loading: boolean;
    onRetry: () => void;
    onSelect: (item: AdminContentItemRpcRow) => void;
}) {
    const adminText = useAdminText();
    if (loading) {
        return <LoadingState label={adminText("Chargement de l’inventaire…")} />;
    }
    if (error) {
        return (
            <ErrorState
                title={adminText("Inventaire indisponible")}
                description={adminText("La liste privée des contenus n’a pas pu être chargée.")}
                retryLabel={adminText("Recommencer")}
                onRetry={onRetry}
            />
        );
    }
    if (items.length === 0) {
        return (
            <EmptyState
                title={adminText("Aucun contenu")}
                description={adminText("Importez l’export Grammaire ou créez un premier brouillon.")}
            />
        );
    }

    return (
        <div className="overflow-x-auto rounded-card border border-line bg-surface">
            <table className="w-full min-w-[42rem] text-left text-sm">
                <thead className="bg-line-soft text-xs uppercase tracking-wide text-muted">
                    <tr>
                        <th className="px-3 py-3">{adminText("Statut")}</th>
                        <th className="px-3 py-3">{adminText("Contenu")}</th>
                        <th className="px-3 py-3">{adminText("Type")}</th>
                        <th className="px-3 py-3">{adminText("Révisions")}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-line">
                    {items.map(item => (
                        <tr key={`${item.content_type}:${item.content_key}`}>
                            <td className="px-3 py-3">
                                <ContentStatusBadge item={item} />
                            </td>
                            <td className="px-3 py-3">
                                <button
                                    type="button"
                                    className="min-h-11 text-left font-bold text-dino-700 hover:underline"
                                    onClick={() => onSelect(item)}
                                >
                                    <span className="block text-ink">{item.title_fr}</span>
                                    <span className="block font-mono text-xs font-normal text-muted">
                                        {item.content_key}
                                    </span>
                                </button>
                            </td>
                            <td className="px-3 py-3">
                                {adminText(contentTypeLabels[item.content_type])}
                                {item.level ? ` · ${item.level}` : ""}
                            </td>
                            <td className="px-3 py-3">
                                {item.published_revision_number ?? "—"} / {item.latest_revision_number}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ContentStatusBadge({ item }: {
    item: AdminContentItemRpcRow;
}) {
    const adminText = useAdminText();
    if (item.archived_at) {
        return <Badge variant="danger">{adminText("Archivé")}</Badge>;
    }
    if (item.latest_revision_number !== item.published_revision_number) {
        return <Badge variant="warning">{adminText("Brouillon")}</Badge>;
    }
    return <Badge variant="success">{adminText("Publié")}</Badge>;
}

export {
    ContentFilters,
    ContentInventory,
    ContentStatusBadge
};
